/**
 * A preposition inside a name is the name's (D242) — `take the coin FROM
 * HOME` when the thing is called "coin from home".
 *
 * @remarks
 * Prepositions are a positional namespace (M1 discovery 3): after a verb that
 * declares `from`, the word binds the phrase after it; after any other verb,
 * a word some intent declares is the `unbound_preposition` teach. Both rules
 * read one word at a time, and so both broke the day `take` began to declare
 * `from` (D242, `take the cell from the beacon`): the island's "coin from
 * home" — a name the pack wrote, one noun of three words — split at its own
 * middle, and `examine the coin from home` was told INSPECT has no use for
 * "from".
 *
 * The rule, as narrow as it can be made: a declared preposition is NAME
 * MATERIAL where the words around it spell, exactly and in order, one of the
 * catalog's own multiword names (a noun or an alias) with that word strictly
 * inside it. The words are the player's as the clause carries them —
 * stopwords already stripped, so a name's stopwords are left out of the
 * comparison too. Nothing is guessed: `take the coin from the tent` spells no
 * name and binds `from` as before. A typed name that is ALSO a binding the
 * player meant — a "coin" taken from a place called "home" — reads as the
 * name; the catalog said the name first.
 *
 * Marked rather than merged: the words stay separate tokens for the resolver,
 * which matches a name against the phrase's words as it always has. The
 * structure matcher reads the mark ({@link isNameMaterial}) and treats the
 * word as phrase material.
 *
 * @packageDocumentation
 */

import { type ParseCandidateEntity } from '../types.js';
import { type CanonicalToken } from './canonicalize-tokens.js';

/** What the marking reads of the compiled vocabulary — `CompiledLexicon` satisfies it structurally. */
export interface NamedPrepositionVocabulary {
	/** Every preposition declared by ANY intent. */
	readonly prepositionTokens: ReadonlySet<string>;
	/** The words stripped before a clause is classified — left out of a name as they are left out of the clause. */
	readonly stopwordSet: ReadonlySet<string>;
}

/**
 * Is this token a declared preposition that sits inside a name the catalog
 * holds, and so is the name's word rather than a binding?
 *
 * @param token - A canonical token
 * @returns Whether the structure matcher reads it as phrase material
 */
export const isNameMaterial = (token: CanonicalToken): boolean => token.kind === 'word' && token.isNameMaterial === true;

/**
 * The catalog's multiword names that carry a declared preposition strictly
 * inside them, each as the words a clause would carry.
 *
 * @param entities - The parse catalog
 * @param vocabulary - The compiled vocabulary view
 * @returns The names' word sequences (empty for almost every catalog)
 */
const namesHoldingPrepositions = (entities: readonly ParseCandidateEntity[], vocabulary: NamedPrepositionVocabulary): readonly (readonly string[])[] =>
	entities
		.flatMap((entity) => [...entity.nouns, ...entity.aliases])
		.map((name) =>
			name
				.toLowerCase()
				.split(/\s+/u)
				.filter((word) => word !== '' && !vocabulary.stopwordSet.has(word)),
		)
		.filter((words) => words.slice(1, -1).some((word) => vocabulary.prepositionTokens.has(word)));

/**
 * Mark every declared preposition that sits inside a name the clause spells.
 *
 * @param tokens - One clause's canonical tokens
 * @param vocabulary - The compiled vocabulary view
 * @param entities - The parse catalog
 * @returns The tokens, the name-held prepositions marked (the same array when there are none)
 */
export const markPrepositionsInsideNames = (
	tokens: readonly CanonicalToken[],
	vocabulary: NamedPrepositionVocabulary,
	entities: readonly ParseCandidateEntity[],
): readonly CanonicalToken[] => {
	const markedIndexes = new Set<number>();

	for (const nameWords of namesHoldingPrepositions(entities, vocabulary)) {
		for (let start = 0; start + nameWords.length <= tokens.length; start += 1) {
			if (nameWords.every((word, offset) => tokens[start + offset]?.sourceText === word)) {
				for (const [offset, word] of nameWords.entries()) {
					if (offset > 0 && offset < nameWords.length - 1 && vocabulary.prepositionTokens.has(word)) {
						markedIndexes.add(start + offset);
					}
				}
			}
		}
	}

	// A direction word inside a name (`in`, `out`) is marked as the plain word it already is mid-phrase.
	return markedIndexes.size === 0
		? tokens
		: tokens.map((token, tokenIndex) =>
			markedIndexes.has(tokenIndex) && (token.kind === 'word' || token.kind === 'direction')
				? { isNameMaterial: true as const, kind: 'word' as const, sourceSpan: token.sourceSpan, sourceText: token.sourceText }
				: token,
		);
};
